import { Controller } from "stimulus";
import { formatMoney } from "utils/money";
import { applyAvatar } from "utils/avatar";

// A busy event can hand a fresh subscriber hundreds of pinned tips via the backfill (the Redis
// hash behind it is uncapped - see LiveTips::RedisKeys#pinned_tips). Every tip stays in `this.tips`
// for correctness (nothing is ever truly discarded client-side), but only the newest RAIL_CAP get a
// DOM chip - each one carries a 20px <img>, so an uncapped rail would load hundreds of avatars.
const RAIL_CAP = 50;

// How long an opened tip stays open before auto-closing. "Show more" cancels this (see toggleExpand).
const CLOSE_DETAIL_MS = 8000;

// A right-edge fade hinting the rail scrolls further, applied only while it actually overflows (see
// refreshFadeMask). Written as one literal arbitrary-value class - Tailwind only generates classes
// it can see verbatim in source, so this can't be assembled from parts at runtime.
const FADE_MASK_CLASS = "[mask-image:linear-gradient(to_right,black_calc(100%-24px),transparent_100%)]";

const COMPACT_CLASSES = ["bg-white", "border", "border-gray-200"];

// The tip chip rail: every pinned tip as a small pill, newest on the left, full message on tap.
// Driven by tip events the chat-panel controller forwards (no subscription of its own), deduped by
// tip id so a broadcast+backfilled tip never renders twice. Replaces the old one-card-at-a-time
// carousel with a rail that shows every pinned tip at once; see docs/live-stream-tipping.md for the
// shape of the tip payload.
export default class PinnedTips extends Controller {
  static targets = ["root", "rail", "detail", "detailName", "detailAmount", "detailBody", "expandToggle", "chipTemplate"];
  static values = { moderator: Boolean };

  connect() {
    this.tips = []; // latest-first, uncapped; index 0 is the newest
    this.seen = new Set(); // every tip id ever recorded (dedupe), independent of the render cap
    this.selectedId = null; // null = detail closed
    this.closeTimer = null;

    // The rail's scrollbar is deliberately hidden (see the template), and touch/trackpad already
    // scroll it natively via a horizontal swipe - but a plain mouse has no horizontal input at all,
    // so an ordinary vertical wheel gesture over the rail would otherwise do nothing. Translate it
    // to horizontal scroll, but only when the gesture is actually vertical (a trackpad's own
    // horizontal swipe already arrives with a deltaX component and is left to the browser) and only
    // while the rail is genuinely overflowing (otherwise let the page scroll normally).
    this.onWheel = (event) => {
      if (Math.abs(event.deltaX) >= Math.abs(event.deltaY)) return;

      const rail = this.railTarget;
      if (rail.scrollWidth <= rail.clientWidth) return;

      event.preventDefault();
      rail.scrollLeft += event.deltaY;
    };
    this.railTarget.addEventListener("wheel", this.onWheel, { passive: false });
  }

  disconnect() {
    this.clearCloseTimer();
    if (this.hasRailTarget) this.railTarget.removeEventListener("wheel", this.onWheel);
  }

  // Backfill on (re)subscribe: the authoritative latest-first set for the session. Fires on every
  // (re)subscribe, including network blips and tab wake, so the DOM is only rebuilt when the
  // rendered id sequence actually changed - otherwise every reconnect would reset horizontal scroll.
  // A backfill can legitimately drop a tip removed while disconnected, so the open detail is closed
  // if its tip didn't survive.
  populate(event) {
    const incoming = event.detail || [];
    const previousSelectedId = this.selectedId;

    this.tips = [];
    this.seen = new Set();
    incoming.forEach((tip) => {
      if (!tip || tip.id == null) return;
      const key = String(tip.id);
      if (this.seen.has(key)) return;
      this.seen.add(key);
      this.tips.push(tip);
    });

    const rendered = this.tips.slice(0, RAIL_CAP);
    const renderedIds = rendered.map((tip) => String(tip.id));
    const currentIds = Array.from(this.railTarget.children).map((node) => node.dataset.tipId);
    const sequenceChanged = renderedIds.length !== currentIds.length
      || renderedIds.some((id, index) => id !== currentIds[index]);

    // renderRail() (when called) already applies chip states onto the freshly built nodes, and
    // nothing between here and there can change selectedId - so there's nothing left to reconcile
    // except a selection that didn't survive the rebuild.
    if (sequenceChanged) this.renderRail(rendered);

    if (previousSelectedId != null && !renderedIds.includes(previousSelectedId)) {
      this.closeDetail();
    }

    this.updateVisibility();
    this.refreshFadeMask();
  }

  // `created` broadcast: the newest tip. Prepend a chip and close any open detail (design: "closes
  // on the next tip arriving"). Inserted incrementally (never a full rebuild), so an in-progress
  // horizontal scroll is never reset by a tip arriving.
  addTip(event) {
    const tip = event.detail;
    if (!tip || tip.id == null) return;

    const key = String(tip.id);
    if (this.seen.has(key)) return; // duplicate (broadcast + backfill overlap)

    this.seen.add(key);
    this.tips.unshift(tip);
    this.closeDetail();

    const rail = this.railTarget;
    const wasAtStart = rail.scrollLeft <= 1;
    // Anchor on the (soon-to-be second) chip rather than measuring total scrollWidth: trimRail can
    // drop a chip off the right end, and applyChipStates resizes the outgoing/incoming featured
    // chip (preview + max-w), both of which would contaminate a whole-track width delta without
    // moving anything left of the viewport.
    const anchor = rail.firstElementChild;
    const anchorLeftBefore = anchor ? anchor.offsetLeft : 0;

    this.prependChipNode(tip);
    this.trimRail();
    this.applyChipStates();

    // If already scrolled to the start, stay there so the new featured chip is visible; otherwise
    // compensate by exactly how far the anchor chip moved, so a reader scrolled into the history
    // isn't yanked.
    if (!wasAtStart && anchor && anchor.isConnected) {
      rail.scrollLeft += anchor.offsetLeft - anchorLeftBefore;
    }

    this.updateVisibility();
    this.refreshFadeMask();
  }

  // `removed` broadcast (moderator remove or a ban sweep): drop the chip by id. Only ever recovers
  // focus for the client that actually had it inside this rail - a remote viewer's removal must
  // never yank focus out of, say, their own composer.
  removeTip(event) {
    const id = event.detail && event.detail.id;
    if (id == null) return;

    const key = String(id);
    const pos = this.tips.findIndex((tip) => String(tip.id) === key);
    if (pos === -1) return;

    this.tips.splice(pos, 1);
    this.seen.delete(key);

    const node = this.chipNodeFor(key);
    const wasSelected = this.selectedId === key;
    const focusWasInside = !!(document.activeElement && this.element.contains(document.activeElement));

    if (wasSelected) this.closeDetail();

    if (node) {
      node.remove();
      this.backfillRailIfNeeded();
    }

    this.applyChipStates();
    this.updateVisibility();
    this.refreshFadeMask();

    if (focusWasInside) this.recoverFocus();
  }

  // Toggle: tapping the open chip again closes it; tapping another opens it in place of whatever
  // was open (addTip already handles "a new tip arriving closes the current one").
  select(event) {
    const chip = event.currentTarget;
    const id = chip.dataset.tipId;
    if (id == null) return;

    if (this.selectedId === id) {
      this.closeDetail();
      return;
    }

    const tip = this.tips.find((candidate) => String(candidate.id) === id);
    if (!tip) return;

    this.selectedId = id;
    this.renderDetail(tip);
    this.detailTarget.classList.remove("hidden");
    this.applyChipStates();
    this.startCloseTimer();
    // `nearest` on both axes: scrollIntoView walks every scrollable ancestor, and the desktop page
    // row is md:overflow-y-auto, so a naive call would scroll the whole page to reveal a 30px chip.
    chip.scrollIntoView({ block: "nearest", inline: "nearest" });
  }

  // "Show more": unclamp in place and cancel the auto-close timer, so a long tip stays readable
  // instead of collapsing out from under someone still reading it.
  toggleExpand() {
    this.clearCloseTimer();
    this.detailBodyTarget.classList.remove("line-clamp-2");
    this.expandToggleTarget.classList.add("hidden");
  }

  // Moderator-only: ask the chat-panel controller (which owns the subscription) to remove the open
  // tip via a window event. We don't drop it locally - the authoritative `removed` broadcast does.
  remove() {
    if (!this.moderatorValue || this.selectedId == null) return;

    const tip = this.tips.find((candidate) => String(candidate.id) === this.selectedId);
    if (!tip) return;

    // Clearing the timer here (not just on "Show more") means the confirm dialog this dispatch
    // opens can never be pulled out from under the user by the detail auto-closing mid-dialog.
    this.clearCloseTimer();

    // The chip - not the trash button - is passed as the confirm dialog's focus-restore trigger: the
    // trash button lives inside the detail card and disappears the moment the detail closes, while
    // the chip survives a Cancel (it's only removed by the authoritative broadcast above).
    const chip = this.chipNodeFor(this.selectedId);
    this.dispatch("remove", { target: window, detail: { id: tip.id, tip, trigger: chip || null } });
  }

  renderDetail(tip) {
    this.detailNameTarget.textContent = (tip.user && tip.user.name) || "";
    this.detailAmountTarget.textContent = formatMoney(tip.amount_cents, tip.currency);
    // textContent, never innerHTML: tip bodies are user input (same XSS invariant the old carousel
    // and the inline tip row both hold).
    this.detailBodyTarget.textContent = (tip.body || "").trim();
    this.detailBodyTarget.classList.add("line-clamp-2");
    this.expandToggleTarget.textContent = "Show more";
    this.refreshExpandToggle();
  }

  // Closes the detail from every path that can trigger it: the same-chip toggle, a new tip
  // arriving, a backfill dropping the open tip, and the 8s timer. addTip/populate call this on
  // EVERY connected client regardless of whether their own detail was open, so recoverFocus() is
  // gated on this client actually having had a selection - otherwise a client who never opened
  // anything, and whose activeElement happens to be <body> (a common idle state), would have focus
  // stolen onto a chip purely because someone else's tip arrived.
  closeDetail() {
    const hadSelection = this.selectedId != null;
    this.clearCloseTimer();
    this.selectedId = null;
    this.detailTarget.classList.add("hidden");
    this.applyChipStates();
    if (hadSelection) this.recoverFocus();
  }

  startCloseTimer() {
    this.clearCloseTimer();
    this.closeTimer = window.setTimeout(() => this.closeDetail(), CLOSE_DETAIL_MS);
  }

  clearCloseTimer() {
    if (this.closeTimer) {
      window.clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  }

  // Checks whether hiding/removing a node just dropped focus to <body> (the DOM change itself
  // unfocuses it, with no event to hook) and if so, moves it to the newest remaining chip so a
  // keyboard/screen-reader user isn't dropped - called from every path that can hide the detail or
  // remove a chip, and a no-op otherwise. Deferred a frame rather than checked synchronously: the
  // ordering against a confirm dialog's own closeConfirm()-driven trigger.focus() isn't guaranteed,
  // and checking synchronously risks a stale recovery immediately being overwritten (the same class
  // of race as the room-closed listener fix in PR #6601).
  recoverFocus() {
    window.requestAnimationFrame(() => {
      const active = document.activeElement;
      const lost = !active || active === document.body || !active.isConnected;
      if (!lost) return;

      const next = this.hasRailTarget ? this.railTarget.firstElementChild : null;
      if (next) next.focus();
    });
  }

  // Full rebuild of the rail's chips, keyed by data-tip-id. Only called from populate() when the
  // rendered id sequence actually changed - addTip/removeTip mutate the DOM incrementally instead,
  // so a tap or an in-progress scroll survives a broadcast.
  renderRail(tips) {
    this.railTarget.replaceChildren();
    tips.forEach((tip) => this.appendChipNode(tip));
    this.applyChipStates();
  }

  prependChipNode(tip) {
    const node = this.buildChipNode(tip);
    const before = this.railTarget.firstElementChild;
    if (before) this.railTarget.insertBefore(node, before);
    else this.railTarget.appendChild(node);
    return node;
  }

  appendChipNode(tip) {
    const node = this.buildChipNode(tip);
    this.railTarget.appendChild(node);
    return node;
  }

  buildChipNode(tip) {
    const node = this.chipTemplateTarget.content.cloneNode(true).firstElementChild;
    node.dataset.tipId = String(tip.id);

    applyAvatar(node.querySelector("[data-chip-avatar]"), node.querySelector("[data-chip-avatar-fallback]"), tip.user);
    node.querySelector("[data-chip-amount]").textContent = formatMoney(tip.amount_cents, tip.currency);
    node.querySelector("[data-chip-preview]").textContent = (tip.body || "").trim();

    return node;
  }

  chipNodeFor(id) {
    return this.railTarget.querySelector(`[data-tip-id="${CSS.escape(String(id))}"]`);
  }

  // Keeps the rail's rendered chips at the RAIL_CAP newest whenever it grows past that (addTip only
  // ever adds at the front, so the oldest rendered chip is always the correct one to drop).
  trimRail() {
    while (this.railTarget.children.length > RAIL_CAP) {
      this.railTarget.lastElementChild.remove();
    }
  }

  // After a removal, if the rail was at RAIL_CAP and a tip beyond the cap exists, that tip has just
  // crossed into visibility - append it so the rail keeps showing the newest RAIL_CAP rather than
  // shrinking by one. `this.tips` and the rail's children are always in the same relative order, so
  // the tip at the current rendered count is exactly the one that just became visible.
  backfillRailIfNeeded() {
    const renderedCount = this.railTarget.children.length;
    if (renderedCount >= RAIL_CAP) return;

    const candidate = this.tips[renderedCount];
    if (candidate) this.appendChipNode(candidate);
  }

  // Only toggles variant classes and the featured chip's preview/width - never rebuilds the chips
  // themselves, so a selection change can't disturb horizontal scroll position.
  applyChipStates() {
    const chips = Array.from(this.railTarget.children);

    chips.forEach((chip) => {
      const id = chip.dataset.tipId;
      const featured = chip === this.railTarget.firstElementChild && this.selectedId == null;
      const selected = this.selectedId === id;
      const accent = featured || selected;

      chip.classList.toggle("bg-accent", accent);
      COMPACT_CLASSES.forEach((cls) => chip.classList.toggle(cls, !accent));
      chip.classList.toggle("max-w-[150px]", featured);

      const amount = chip.querySelector("[data-chip-amount]");
      amount.classList.toggle("text-accent-text", accent);
      amount.classList.toggle("text-strong-text", !accent);

      const preview = chip.querySelector("[data-chip-preview]");
      preview.classList.toggle("hidden", !featured);
      preview.classList.toggle("text-accent-text", featured);
    });
  }

  updateVisibility() {
    this.rootTarget.classList.toggle("hidden", this.tips.length === 0);
  }

  // The overflow fade is an affordance for "there's more to scroll", not a tip state - only ever on
  // while the track genuinely overflows. Measured on the next frame so layout has settled.
  refreshFadeMask() {
    window.requestAnimationFrame(() => {
      if (!this.hasRailTarget) return;
      const overflowing = this.railTarget.scrollWidth > this.railTarget.clientWidth + 1;
      this.railTarget.classList.toggle(FADE_MASK_CLASS, overflowing);
    });
  }

  // Show "Show more" only when the clamped body overflows two lines (measured on the next frame so
  // layout is settled after the text swap).
  refreshExpandToggle() {
    window.requestAnimationFrame(() => {
      if (!this.hasExpandToggleTarget || !this.hasDetailBodyTarget) return;
      const overflowing = this.detailBodyTarget.scrollHeight > this.detailBodyTarget.clientHeight + 1;
      this.expandToggleTarget.classList.toggle("hidden", !overflowing);
    });
  }
}
